import React from 'react'
import { Box, Button, Link, MenuItem } from '@mui/material'
import { SxProps, Theme } from '@mui/material'
import FormField from 'components/FormField'
import { AdminPracticeCreationForm, AdminPracticeCreationResult } from 'types/interfaces'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import AdminPracticeCreationFormError from 'errors/AdminPracticeCreationFormError'
import { MARKETING_CHANNELS } from 'types/constants'
import Alert from '@mui/material/Alert'
import { PaymentOption } from 'types/types'
import AddressSearch from 'components/AddressSearch'
import { adminPracticeDetailURL } from 'routes/urls'
import SwitchField from 'components/SwitchField'

const style: SxProps<Theme> = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: ['80%', '80%', '50%'],
    maxHeight: '90%',
    bgcolor: 'background.default',
    border: '2px solid #000',
    boxShadow: 24,
    padding: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1
}

interface CreatePracticeModalProps {
  onCancel: () => void
}

const CreatePracticeModal: React.FC<CreatePracticeModalProps> = ({
  onCancel
}) => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [form, setForm] = React.useState<AdminPracticeCreationForm>({
        tz: 'Europe/London',
        invoice_due_days: 1,
        default_settlement_days: 15,
        start_time: '09:00',
        end_time: '17:00',
        lunch_break: 60,
        description: '',
    })
    const [errors, setErrors] = React.useState<AdminPracticeCreationFormError>()
    const [loading, setLoading] = React.useState<boolean>(false)
    const [result, setResult] = React.useState<AdminPracticeCreationResult>()
    const [manualAddress, setManualAddress] = React.useState(false)

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
        if (errors) setErrors(update(errors, { [name]: { $set: [] } }))
    }, [form, errors])

    const save = React.useCallback(async () => {
        try {
            setLoading(true)
            const result = await api.adminCreatePractice(form)
            setResult(result)
        } catch (e) {
            if (e instanceof AdminPracticeCreationFormError) {
                setErrors(e)
            } else {
                showAlert('error', 'Error creating practice')
                console.error('unhandled exception', e)
            }
        } finally {
            setLoading(false)
        }
    }, [api, form, showAlert, setResult])

    // For postcode geocoding
    const prepopulateCoordinates = React.useCallback(async () => {
        try {
            if (!form.postcode) return
            const coordinates = await api.getCoordinatesFromPostcode(form.postcode)
            setForm(update(form, {
                latitude: { $set: coordinates.latitude },
                longitude: { $set: coordinates.longitude },
            }))
        } catch (e) {
            showAlert('error', 'Error getting coordinates from postcode')
            console.error('error getting coordinates from postcode', e)
        }
    }, [api, form, showAlert])

    // Helper to join address fields
    const updateManualAddress = React.useCallback((name: string, value: any) => {
        setForm(prev => {
            const updated = update(prev, { [name]: { $set: value } })
            // Join all non-empty address parts
            const parts = [
                updated.line1,
                updated.line2,
                updated.city,
                updated.county,
                updated.country,
                updated.postcode
            ].filter(Boolean)
            return update(updated, { address: { $set: parts.join('\n') } })
        })
        if (errors) setErrors(update(errors, { [name]: { $set: [] } }))
    }, [errors])

    const paymentOptions: { value: PaymentOption; label: string }[] = [
        { value: 'invoice_weekly_pay_manually', label: 'Invoice Weekly Pay Manually' },
        { value: 'invoice_biweekly_pay_manually', label: 'Invoice Biweekly Pay Manually' },
        { value: 'invoice_monthly_pay_manually', label: 'Invoice Monthly Pay Manually' },
    ]

    return (
        <Box sx={style}>
            <FormField
                name='name'
                label='Practice Name'
                value={form.name ?? ''}
                onChange={(e) => updateForm('name', e.target.value)}
                errors={errors?.name}
            />
            <FormField
                name='email'
                label='User Email'
                value={form.email ?? ''}
                onChange={(e) => updateForm('email', e.target.value)}
                errors={errors?.email}
            />
            <FormField
                name='first_name'
                label='User First Name'
                value={form.first_name ?? ''}
                onChange={(e) => updateForm('first_name', e.target.value)}
                errors={errors?.first_name}
            />
            <FormField
                name='last_name'
                label='User Last Name'
                value={form.last_name ?? ''}
                onChange={(e) => updateForm('last_name', e.target.value)}
                errors={errors?.last_name}
            />            
            <FormField
                name='affiliate_code'
                label='Affiliate Code'
                value={form.affiliate_code ?? ''}
                onChange={(e) => updateForm('affiliate_code', e.target.value)}
                errors={errors?.affiliate_code}
            />
            <FormField
                select
                name='marketing_channel'
                label='How did you hear about Locumloop?'
                value={form.marketing_channel ?? ''}
                onChange={(e) => updateForm('marketing_channel', e.target.value)}
                errors={errors?.marketing_channel}
            >
              <MenuItem value=''>Select Channel</MenuItem>
              {MARKETING_CHANNELS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </FormField>
            <SwitchField
                label="Enter address manually"
                value={manualAddress}
                onChange={setManualAddress}
            />
            {manualAddress ? (
                <>
                    <FormField
                        name='line1'
                        label='Address Line 1'
                        value={form.line1 ?? ''}
                        onChange={e => updateManualAddress('line1', e.target.value)}
                        errors={errors?.line1}
                    />
                    <FormField
                        name='line2'
                        label='Address Line 2'
                        value={form.line2 ?? ''}
                        onChange={e => updateManualAddress('line2', e.target.value)}
                        errors={errors?.line2}
                    />
                    <FormField
                        name='city'
                        label='City'
                        value={form.city ?? ''}
                        onChange={e => updateManualAddress('city', e.target.value)}
                        errors={errors?.city}
                    />
                    <FormField
                        name='county'
                        label='County'
                        value={form.county ?? ''}
                        onChange={e => updateManualAddress('county', e.target.value)}
                        errors={errors?.county}
                    />
                    <FormField
                        name='country'
                        label='Country'
                        value={form.country ?? ''}
                        onChange={e => updateManualAddress('country', e.target.value)}
                        errors={errors?.country}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        <FormField
                            name='postcode'
                            label='Postcode'
                            value={form.postcode ?? ''}
                            onChange={e => updateManualAddress('postcode', e.target.value)}
                            errors={errors?.postcode}
                            sx={{ flex: 1 }}
                        />
                        <Button variant='outlined' color='primary' onClick={prepopulateCoordinates}>
                            Get Coordinates
                        </Button>
                    </Box>
                    <FormField
                        name='latitude'
                        label='Latitude'
                        type='number'
                        value={form.latitude ?? ''}
                        onChange={e => updateManualAddress('latitude', e.target.value ? Number(e.target.value) : undefined)}
                        errors={errors?.latitude}
                    />
                    <FormField
                        name='longitude'
                        label='Longitude'
                        type='number'
                        value={form.longitude ?? ''}
                        onChange={e => updateManualAddress('longitude', e.target.value ? Number(e.target.value) : undefined)}
                        errors={errors?.longitude}
                    />
                </>
            ) : (
                <AddressSearch
                    label='Address'
                    form={form}
                    error={errors}
                    setForm={setForm}
                />
            )}
            <FormField
                name='email_for_inquiries'
                label='Email (General Inquiries)'
                value={form.email_for_inquiries ?? ''}
                onChange={(e) => updateForm('email_for_inquiries', e.target.value)}
                errors={errors?.email_for_inquiries}
            />
            <FormField
                name='email_for_notifications'
                label='Email (to receive notifications)'
                value={form.email_for_notifications ?? ''}
                onChange={(e) => updateForm('email_for_notifications', e.target.value)}
                errors={errors?.email_for_notifications}
            />
            <FormField
                name='phone_for_inquiries'
                label='Phone Number (General Inquiries)'
                value={form.phone_for_inquiries ?? ''}
                onChange={(e) => updateForm('phone_for_inquiries', e.target.value)}
                errors={errors?.phone_for_inquiries}
                helperText='format: (+44)1234512345 or 01234512345)'
            />
            <FormField
                name='phone_for_notifications'
                label='Phone Number (to receive SMS notifications)'
                value={form.phone_for_notifications ?? ''}
                onChange={(e) => updateForm('phone_for_notifications', e.target.value)}
                errors={errors?.phone_for_notifications}
                helperText='format: (+44)1234512345 or 01234512345)'
            />
            <FormField
                name='billing_name'
                label='Billing Name'
                value={form.billing_name ?? ''}
                onChange={(e) => updateForm('billing_name', e.target.value)}
                errors={errors?.billing_name}
            />
            <FormField
                name='billing_email'
                label='Billing Email'
                value={form.billing_email ?? ''}
                onChange={(e) => updateForm('billing_email', e.target.value)}
                errors={errors?.billing_email}
            />
            <FormField
                select
                name='payment_option'
                label='Payment Option'
                value={form.payment_option ?? ''}
                onChange={(e) => updateForm('payment_option', e.target.value)}
                errors={errors?.payment_option}
            >
              <MenuItem value=''>Select Payment Option</MenuItem>
              {paymentOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </FormField>
            <FormField
                name='invoice_due_days'
                label='Invoice Due Days'
                type='number'
                value={form.invoice_due_days ?? 1}
                onChange={(e) => updateForm('invoice_due_days', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.invoice_due_days}
                helperText="The due date printed on invoices for this practice"
            />
            <FormField
                name='default_settlement_days'
                label='Expected Settlement Days'
                type='number'
                value={form.default_settlement_days ?? 15}
                onChange={(e) => updateForm('default_settlement_days', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.default_settlement_days}
                helperText="The realistic expected days that the practice will pay the invoice after receiving it. This is to set the expectation of the nurses"
            />
            { result ? (
                <Alert severity="success" variant='filled' sx={{ mb: 2 }}>
                    <div>Practice created successfully!</div>
                    <div>Practice ID: <Link href={adminPracticeDetailURL(result.practice_id)}>{result.practice_id}</Link></div>
                    <div>User Email: <b>{result.user_email}</b></div>
                    <div>User Password: <b>{result.user_password}</b></div>
                </Alert>
            ) : (
                <>
                    <Button variant='contained' color='primary' onClick={save} disabled={loading} sx={{ mt: 2 }}>
                        {loading ? 'Please wait...' : 'Create Practice'}
                    </Button>
                    <Button variant='outlined' color='primary' onClick={onCancel} disabled={loading} sx={{ mt: 1 }}>
                        Cancel
                    </Button>
                </>
            ) }
        </Box>
    )
}

export default CreatePracticeModal